import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export interface UserPreferences {
  id?: string;
  user_id: string;
  tenant_id?: string;
  language_code: string;
  time_zone: string;
  date_format: string;
  email_notifications: boolean;
  sms_notifications: boolean;
  desktop_notifications: boolean;
  sidebar_collapsed: boolean;
  reduced_motion: boolean;
  high_contrast: boolean;
  data_retention_period: string;
  auto_backups: boolean;
  export_format: string;
  setup_completed: boolean;
  setup_completed_at?: string;
}

export const useUserPreferences = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: preferences, isLoading } = useQuery({
    queryKey: ['user-preferences', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data;
    },
    enabled: !!user?.id,
  });

  const updatePreferencesMutation = useMutation({
    mutationFn: async (newPreferences: Partial<UserPreferences>) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          ...newPreferences,
        }, {
          onConflict: 'user_id'
        })
        .select()
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-preferences'] });
      toast.success('Preferences updated successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to update preferences: ${error.message}`);
    },
  });

  const resetToDefaultsMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      const defaults: Partial<UserPreferences> = {
        language_code: 'en',
        time_zone: 'utc',
        date_format: 'iso',
        email_notifications: true,
        sms_notifications: false,
        desktop_notifications: true,
        sidebar_collapsed: false,
        reduced_motion: false,
        high_contrast: false,
        data_retention_period: '7years',
        auto_backups: true,
        export_format: 'pdf',
        setup_completed: false,
      };

      const { data, error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          ...defaults,
        }, {
          onConflict: 'user_id'
        })
        .select()
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-preferences'] });
      toast.success('Preferences reset to defaults');
    },
    onError: (error: any) => {
      toast.error(`Failed to reset preferences: ${error.message}`);
    },
  });

  const updatePreferences = (newPreferences: Partial<UserPreferences>) => {
    updatePreferencesMutation.mutate(newPreferences);
  };

  const resetToDefaults = () => {
    resetToDefaultsMutation.mutate();
  };

  const markSetupCompleted = () => {
    updatePreferences({
      setup_completed: true,
      setup_completed_at: new Date().toISOString(),
    });
  };

  return {
    preferences,
    isLoading,
    updatePreferences,
    resetToDefaults,
    markSetupCompleted,
    isUpdating: updatePreferencesMutation.isPending,
    isResetting: resetToDefaultsMutation.isPending,
    hasCompletedSetup: preferences?.setup_completed || false,
  };
};